import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { createPortal } from 'react-dom';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { PrimaryButton, SecondaryButton } from "../UI";
import { toast } from 'react-toastify';
import { getAdobeAnalyticsDashboardCards, saveAdobeAnalyticsDashboardCards, getAdobeUsersMetadataKeys } from '../../utils/APIs';
import { rgbToHex } from '../../utils/helper';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import Loader from '../UI/Loader/Loader';
import AgGridWrapper from '../UI/AgGrid';
import ColorPicker from 'react-best-gradient-color-picker';
import ConfirmModal from '../UI/Modal/confirmModal';
import { RotateCcw, Trash2, Plus, X } from 'lucide-react';

// All possible event filter keys with human-readable labels
const FALLBACK_EVENT_FILTERS = [
    { key: 'login', label: 'Logins' },
    { key: 'viewed', label: 'Project Views' },
    { key: 'created', label: 'Project Created' },
    { key: 'updated', label: 'Project Updated' },
    { key: 'shared', label: 'Project Shared' },
    { key: 'segment_created', label: 'Segment Created' },
    { key: 'segment_updated', label: 'Segment Updated' },
    { key: 'calc_metric_created', label: 'Calc Metric Created' },
    { key: 'calc_metric_updated', label: 'Calc Metric Updated' },
];

const TARGET_EVENT_FILTERS = [
    { key: 'create', label: 'Create' },
    { key: 'update', label: 'Update' },
    { key: 'patch', label: 'Patch' }
];

const EMPTY_CARD = {
    label: 'NEW CARD',
    sequence: 1,
    eventType: '',
    width: 'half',
    count_header: 'Count',
    total_count_header: 'Total Count',
    help_text: '',
    group_by: 'user',
    event_filters: ['login'],
    metadata_key: '',
    is_visible: true,
    display_mode: 'list',
    color: '#465DAE',
};

// ── Chips renderer: shows event_filters array as single-line capsule tags with overflow counter & tooltip ──
const EventFilterRenderer = (props) => {
    const { value, clickToSelect = 'Click to select', eventFiltersList, column, node, api } = props;
    if (!Array.isArray(value) || value.length === 0) {
        return <span style={{ color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>{clickToSelect}</span>;
    }

    const list = eventFiltersList || FALLBACK_EVENT_FILTERS;
    const items = value.map(k => ({
        key: k,
        label: list.find(f => f.key === k)?.label || k
    }));
    const fullText = items.map(i => i.label).join(', ');

    const colWidth = column?.getActualWidth ? column.getActualWidth() : 220;
    const availableWidth = Math.max(colWidth - 28, 60);

    const estimateWidth = (str) => Math.ceil(str.length * 7.5) + 32;
    const MORE_PILL_WIDTH = 68;

    let allWidth = 0;
    items.forEach((item, i) => {
        allWidth += estimateWidth(item.label) + (i > 0 ? 6 : 0);
    });

    let visibleItems = [];
    let remainingCount = 0;

    if (allWidth <= availableWidth) {
        visibleItems = items;
        remainingCount = 0;
    } else {
        let fitCount = 0;
        let currentWidth = 0;
        for (let i = 0; i < items.length; i++) {
            const itemWidth = estimateWidth(items[i].label) + 6;
            if (currentWidth + itemWidth + MORE_PILL_WIDTH <= availableWidth) {
                currentWidth += itemWidth;
                fitCount++;
            } else {
                break;
            }
        }
        visibleItems = items.slice(0, Math.max(fitCount, 1));
        remainingCount = items.length - visibleItems.length;
    }

    const handleRemoveFilter = (e, keyToRemove) => {
        e.stopPropagation();
        if (!node || node.data?.is_deleted) return;
        const newValue = value.filter(k => k !== keyToRemove);
        node.setDataValue('event_filters', newValue);
        if (api) {
            api.refreshCells({ rowNodes: [node], force: true });
        }
    };

    return (
        <div
            className="flex items-center gap-1.5 h-full overflow-hidden whitespace-nowrap text-ellipsis cursor-pointer py-1"
            title={fullText}
        >
            {visibleItems.map((item) => (
                <span
                    key={item.key}
                    className="inline-flex items-center gap-1 text-xs font-medium pl-2.5 pr-1 py-0.5 rounded-full bg-primary-2/10 text-primary-2 whitespace-nowrap shrink-0"
                >
                    <span>{item.label}</span>
                    <button
                        type="button"
                        onClick={(e) => handleRemoveFilter(e, item.key)}
                        onDoubleClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center justify-center p-0.5 rounded-full hover:bg-primary-2/20 text-primary-2/70 hover:text-primary-2 focus:outline-none transition-colors cursor-pointer"
                        title={`Remove ${item.label}`}
                    >
                        <X className="w-3 h-3" />
                    </button>
                </span>
            ))}
            {remainingCount > 0 && (
                <span
                    className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap shrink-0 hover:bg-gray-200"
                    title={fullText}
                >
                    +{remainingCount} more
                </span>
            )}
        </div>
    );
};

// ── Popup checkbox editor for event_filters ──
// isPopup() = true → AgGrid renders this outside the cell so it isn't clipped
const EventFilterEditor = forwardRef(({ value, stopEditing, title = 'Select Event Filters', doneText = 'Done', eventFiltersList }, ref) => {
    const [selected, setSelected] = useState(new Set(Array.isArray(value) ? value : []));

    useImperativeHandle(ref, () => ({
        getValue: () => [...selected],
        isPopup: () => true,         // ← key fix: renders as floating popup
        isCancelBeforeStart: () => false,
        isCancelAfterEnd: () => false,
    }));

    const toggle = (key) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const list = eventFiltersList || FALLBACK_EVENT_FILTERS;

    return (
        <div
            style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                padding: '12px 14px',
                minWidth: 230,
                zIndex: 9999,
            }}
        >
            <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                {title}
            </p>
            {list.map(({ key, label }) => (
                <label
                    key={key}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '5px 4px', cursor: 'pointer', borderRadius: 4,
                        fontSize: 13, color: '#374151',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                    <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggle(key)}
                        style={{ accentColor: '#4f46e5', width: 14, height: 14 }}
                    />
                    {label}
                </label>
            ))}
            <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 8, paddingTop: 8, textAlign: 'right' }}>
                <button
                    onClick={() => stopEditing()}
                    style={{
                        padding: '4px 12px', fontSize: 12, fontWeight: 600,
                        color: '#fff', background: '#4f46e5', border: 'none',
                        borderRadius: 5, cursor: 'pointer',
                    }}
                >
                    {doneText}
                </button>
            </div>
        </div>
    );
});
EventFilterEditor.displayName = 'EventFilterEditor';

// ── Color cell renderer ──
const ColorCellRenderer = ({ value }) => {
    const displayColor = (rgbToHex(value) || '#465DAE').toUpperCase();
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%' }}>
            <span
                style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: displayColor,
                    border: '1px solid rgba(0,0,0,0.15)',
                    display: 'inline-block',
                    flexShrink: 0
                }}
            />
            <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#374151', fontWeight: 500 }}>
                {displayColor}
            </span>
        </div>
    );
};

// ── Color cell editor (Hex solid color picker only) ──
const ColorCellEditor = forwardRef(({ value, stopEditing }, ref) => {
    const initialHex = (rgbToHex(value) || '#465DAE').toUpperCase();
    const [color, setColor] = useState(initialHex);

    const handleColorChange = (newVal) => {
        if (typeof newVal === 'string') {
            setColor((rgbToHex(newVal) || '#465DAE').toUpperCase());
        } else {
            setColor(newVal);
        }
    };

    const currentHex = (rgbToHex(color) || '#465DAE').toUpperCase();

    useImperativeHandle(ref, () => ({
        getValue: () => currentHex,
        isPopup: () => true,
        isCancelBeforeStart: () => false,
        isCancelAfterEnd: () => false,
    }));

    return createPortal(
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black bg-opacity-40">
            <div className="w-full max-w-sm flex flex-col items-center rounded-lg bg-white p-6 shadow-xl border border-gray-200">
                <h3 className="mb-3 text-sm font-bold text-gray-700 uppercase tracking-wider self-start">
                    Select Hex Color
                </h3>
                <div className="flex justify-center mb-4 overflow-hidden">
                    <ColorPicker
                        value={color}
                        onChange={handleColorChange}
                        hideGradientControls={true}
                        hideColorTypeBtns={true}
                        hidePresets={true}
                        width={280}
                    />
                </div>
                <div className="mt-2 flex items-center justify-between w-full pt-3 border-t border-gray-100">
                    <span className="text-xs font-mono font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        {currentHex}
                    </span>
                    <button
                        type="button"
                        className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-2 focus:outline-none"
                        onClick={() => stopEditing()}
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
});
ColorCellEditor.displayName = 'ColorCellEditor';

// ── Visible boolean renderer ──
const VisibleRenderer = ({ value, visibleLabel = '● Visible', hiddenLabel = '○ Hidden' }) => (
    <span style={{ fontSize: 12, fontWeight: 600, color: value !== false ? '#16a34a' : '#9ca3af' }}>
        {value !== false ? visibleLabel : hiddenLabel}
    </span>
);

const CustomHeader = forwardRef((props, ref) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const iconRef = useRef(null);
    const tooltipText = props.tooltipText;

    const handleMouseEnter = () => {
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            setTooltipPos({
                top: rect.top - 8,
                left: rect.left + rect.width / 2
            });
        }
        setShowTooltip(true);
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{props.displayName}</span>
            {tooltipText && (
                <div 
                    ref={iconRef}
                    style={{ position: 'relative', display: 'flex', alignItems: 'center', marginLeft: '4px', cursor: 'pointer' }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    {showTooltip && createPortal(
                        <div style={{
                            position: 'fixed',
                            top: tooltipPos.top,
                            left: tooltipPos.left,
                            transform: 'translate(-50%, -100%)',
                            padding: '8px 12px',
                            backgroundColor: '#1f2937',
                            color: '#fff',
                            fontSize: '12px',
                            borderRadius: '6px',
                            whiteSpace: 'normal',
                            width: 'max-content',
                            maxWidth: '250px',
                            zIndex: 999999,
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            lineHeight: '1.4',
                            fontWeight: 'normal',
                            pointerEvents: 'none'
                        }}>
                            {tooltipText}
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                borderWidth: '5px',
                                borderStyle: 'solid',
                                borderColor: '#1f2937 transparent transparent transparent'
                            }}></div>
                        </div>,
                        document.body
                    )}
                </div>
            )}
        </div>
    );
});
CustomHeader.displayName = 'CustomHeader';

const ManageDashboardCardsModal = ({ closeManageCardsModal, onSaveSuccess, dashboardId = 'adobe_analytics' }) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();
    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [selectedCount, setSelectedCount] = useState(0);
    const [metadataKeys, setMetadataKeys] = useState([]);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [deleteTargetNodes, setDeleteTargetNodes] = useState([]);

    const originalDataRef = useRef([]);

    const orgId = userData?.selectedOrganization?.org_token;
    const token = userData?.token;

    const UI_CONTENT = content?.ADOBE_ANALYTICS_USAGE_CONTENT?.manage_modal || {
        "title": "Manage Dashboard Cards",
        "loading_cards": "Loading cards…",
        "fetching_cards": "Fetching dashboard cards…",
        "no_cards_msg": "No cards yet — click + Add Card to get started.",
        "double_click_instruction": "Double-click a cell to edit",
        "delete_btn": "Delete",
        "deleting_btn": "Deleting…",
        "add_card_btn": "+ Add Card",
        "cancel_btn": "Cancel",
        "save_btn": "Save Changes",
        "saving_btn": "Saving…",
        "select_filters_title": "Select Event Filters",
        "done_btn": "Done",
        "click_to_select": "Click to select",
        "visible_label": "● Visible",
        "hidden_label": "○ Hidden",
        "headers": {
            "sequence": "#",
            "label": "Label",
            "eventType": "Event Type",
            "group_by": "Group By",
            "event_filters": "Event Filters",
            "display_mode": "Display",
            "metadata_key": "Metadata Key",
            "is_visible": "Visible",
            "width": "Width",
            "count_header": "Count Header",
            "total_count_header": "Total Count Header",
            "help_text": "Help Text"
        },
        "validation": {
            "required_fields": "Label, Width, Count Header and Total Count Header are required.",
            "group_by_required": "Group By is required.",
            "filters_required": "At least one Event Filter must be selected.",
            "metadata_key_required": "Metadata Key is required when Group By is \"metadata_key\".",
            "select_to_delete": "Select at least one card to delete.",
            "delete_success": "Deleted {count} card(s)",
            "remove_success": "Removed {count} unsaved card(s)",
            "delete_failed": "Failed to delete cards",
            "fetch_failed": "Failed to fetch dashboard config.",
            "save_success": "Dashboard cards saved successfully!",
            "save_failed": "Failed to save dashboard cards."
        }
    };

    useEffect(() => { fetchCards(); }, []); // eslint-disable-line

    const fetchCards = async () => {
        try {
            if (!orgId || !token) return;
            setLoading(true);
            const [cardsRes, keysRes] = await Promise.all([
                getAdobeAnalyticsDashboardCards(token, orgId, dashboardId),
                getAdobeUsersMetadataKeys(token, orgId),
            ]);
            const cleanedCards = (cardsRes?.data || []).map(card => {
                const { organization, __v, ...rest } = card;
                return {
                    ...rest,
                    eventType: rest.eventType || '',
                    group_by: rest.group_by || '',
                    event_filters: Array.isArray(rest.event_filters) ? rest.event_filters : [],
                    is_visible: rest.is_visible !== false,
                    display_mode: rest.display_mode || 'list',
                };
            });
            originalDataRef.current = JSON.parse(JSON.stringify(cleanedCards));
            setRowData(cleanedCards);
            setMetadataKeys(['', ...(keysRes?.data || [])]);
        } catch (error) {
            toast.error(error?.message || UI_CONTENT.validation.fetch_failed);
        } finally {
            setLoading(false);
        }
    };

    const collectRows = () => {
        if (!gridApi) return null;
        gridApi.stopEditing();
        const rows = [];
        gridApi.forEachNode(node => rows.push({ ...node.data }));
        return rows;
    };

    const handleSave = async () => {
        const rows = collectRows();
        if (!rows) return;
        
        // Filter out soft-deleted rows before saving to DB
        const activeRows = rows.filter(r => !r.is_deleted);
        activeRows.forEach((row, i) => { row.sequence = i + 1; });

        for (let i = 0; i < activeRows.length; i++) {
            const row = activeRows[i];
            if (!row.label || !row.width || !row.count_header || !row.total_count_header) {
                toast.error(`Row ${i + 1}: ${UI_CONTENT.validation.required_fields}`);
                return;
            }
            if (!row.group_by) {
                toast.error(`Row ${i + 1}: ${UI_CONTENT.validation.group_by_required}`);
                return;
            }
            if (!Array.isArray(row.event_filters) || row.event_filters.length === 0) {
                toast.error(`Row ${i + 1}: ${UI_CONTENT.validation.filters_required}`);
                return;
            }
            if (row.group_by === 'metadata_key' && !row.metadata_key) {
                toast.error(`Row ${i + 1}: ${UI_CONTENT.validation.metadata_key_required}`);
                return;
            }
        }

        try {
            setSaving(true);
            const res = await saveAdobeAnalyticsDashboardCards(token, orgId, activeRows, dashboardId);
            toast.success(UI_CONTENT.validation.save_success);
            onSaveSuccess(res?.data || activeRows);
            closeManageCardsModal();
        } catch (error) {
            toast.error(error?.message || UI_CONTENT.validation.save_failed);
        } finally {
            setSaving(false);
        }
    };

    const handleAddCard = () => {
        if (!gridApi) { setRowData([{ ...EMPTY_CARD, sequence: 1 }]); return; }
        let maxSeq = 0;
        gridApi.forEachNode(node => { if ((node.data?.sequence || 0) > maxSeq) maxSeq = node.data.sequence; });
        gridApi.applyTransaction({ add: [{ ...EMPTY_CARD, sequence: maxSeq + 1 }] });
        setTimeout(() => {
            const lastIdx = gridApi.getDisplayedRowCount() - 1;
            gridApi.ensureIndexVisible(lastIdx, 'bottom');
            gridApi.startEditingCell({ rowIndex: lastIdx, colKey: 'label' });
        }, 80);
    };

    const handleDeleteButtonClick = () => {
        if (!gridApi) return;
        const selectedNodes = gridApi.getSelectedNodes();
        if (selectedNodes.length === 0) { toast.info(UI_CONTENT.validation.select_to_delete); return; }
        setDeleteTargetNodes(selectedNodes);
        setShowDeleteConfirmModal(true);
    };

    const handleSingleRowDeleteClick = useCallback((node) => {
        if (!node) return;
        setDeleteTargetNodes([node]);
        setShowDeleteConfirmModal(true);
    }, []);

    const handleAddRowBelow = useCallback((node) => {
        if (!gridApi || !node) return;
        const targetIdx = node.rowIndex;
        const newCard = { ...EMPTY_CARD, sequence: (node.data?.sequence || 0) + 1 };
        gridApi.applyTransaction({ add: [newCard], addIndex: targetIdx + 1 });
        setTimeout(() => {
            const newRowIdx = targetIdx + 1;
            gridApi.ensureIndexVisible(newRowIdx, 'middle');
            gridApi.startEditingCell({ rowIndex: newRowIdx, colKey: 'label' });
        }, 80);
    }, [gridApi]);

    const handleConfirmDelete = () => {
        if (deleteTargetNodes && deleteTargetNodes.length > 0) {
            deleteTargetNodes.forEach(node => {
                if (node && node.data) {
                    node.data.is_deleted = true;
                }
            });
            if (gridApi) {
                gridApi.deselectAll();
                gridApi.redrawRows();
            }
        }
        setShowDeleteConfirmModal(false);
        setDeleteTargetNodes([]);
        setSelectedCount(0);
    };

    const isCellDirty = useCallback((data, colId) => {
        if (!data) return false;
        if (data.is_deleted) return true;
        if (!data._id) return true; // New unsaved row
        const original = originalDataRef.current?.find(r => r._id === data._id);
        if (!original) return true;
        const origVal = original[colId];
        const currVal = data[colId];
        if (colId === 'event_filters') {
            const oArr = Array.isArray(origVal) ? origVal : [];
            const cArr = Array.isArray(currVal) ? currVal : [];
            if (oArr.length !== cArr.length) return true;
            const oSorted = [...oArr].sort();
            const cSorted = [...cArr].sort();
            return oSorted.some((v, idx) => v !== cSorted[idx]);
        }
        return String(origVal ?? '') !== String(currVal ?? '');
    }, []);

    const onSelectionChanged = useCallback(() => {
        if (!gridApi) return;
        setSelectedCount(gridApi.getSelectedRows().length);
    }, [gridApi]);

    const onCellValueChanged = useCallback((params) => {
        const colId = params.column.getColId();
        const data = params.data;

        if (colId === 'eventType') {
            if (params.newValue === '100') {
                params.node.setDataValue('group_by', 'metadata_key');
            } else {
                if (data.group_by === 'metadata_key') {
                    params.node.setDataValue('group_by', 'user');
                }
                params.node.setDataValue('metadata_key', '');
            }
        }

        if (colId === 'group_by') {
            if (params.newValue === 'metadata_key') {
                params.node.setDataValue('eventType', '100');
            } else {
                if (data.eventType === '100') {
                    params.node.setDataValue('eventType', '2'); // Default to login (user)
                }
                params.node.setDataValue('metadata_key', '');
            }
        }

        if (colId === 'group_by' && params.newValue !== 'metadata_key') {
            params.node.setDataValue('metadata_key', '');
        }

        params.api.refreshCells({ rowNodes: [params.node], force: true });
    }, []);

    const onRowDragEnd = useCallback(() => {
        if (!gridApi) return;
        const nodes = [];
        gridApi.forEachNode(node => nodes.push(node));
        nodes.forEach((node, index) => {
            if (node.data.sequence !== index + 1) {
                node.setDataValue('sequence', index + 1);
            }
        });
        gridApi.refreshCells({ force: true });
    }, [gridApi]);

    const getCellStyle = useCallback((colId) => (params) => {
        if (!params.data) return null;
        if (params.data.is_deleted) {
            return { background: '#fef2f2', color: '#9ca3af', textDecoration: 'line-through' };
        }
        // Disability rules
        if (colId === 'group_by' && params.data.eventType === '100') {
            return { background: '#f3f4f6', color: '#9ca3af' };
        }
        if (colId === 'metadata_key' && params.data.eventType !== '100') {
            return { background: '#f3f4f6', color: '#9ca3af' };
        }
        // Dirty rule (exclude event_filters so chip badges remain clean when filters are modified/de-selected)
        if (colId !== 'event_filters' && isCellDirty(params.data, colId)) {
            return { background: '#fef08a', color: '#1e293b' }; // yellow-200
        }
        return null;
    }, [isCellDirty]);

    const eventFiltersList = useMemo(() => {
        if (dashboardId === 'adobe_target') return TARGET_EVENT_FILTERS;
        return content?.ADOBE_ANALYTICS_USAGE_CONTENT?.all_event_filters || FALLBACK_EVENT_FILTERS;
    }, [dashboardId, content]);

    const columnDefs = useMemo(() => [
        {
            headerCheckboxSelection: true,
            checkboxSelection: true,
            width: 50, minWidth: 50, maxWidth: 50,
            pinned: 'left', filter: false, sortable: false,
            resizable: false, suppressMenu: true,
        },
        {
            headerName: UI_CONTENT.headers.sequence, field: 'sequence',
            editable: false, width: 80, type: 'numericColumn', rowDrag: true,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'Order in which the card appears on the dashboard.' },
            cellStyle: getCellStyle('sequence'),
        },
        {
            headerName: UI_CONTENT.headers.label, field: 'label',
            editable: params => !params.data?.is_deleted, flex: 1, minWidth: 160,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'The title of the card displayed on the dashboard.' },
            cellStyle: getCellStyle('label'),
        },
        {
            headerName: UI_CONTENT.headers.eventType, field: 'eventType',
            editable: params => !params.data?.is_deleted, width: 110,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['2', '23', '24', '25', '100'] },
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'Adobe API event type code (2=login, 23=activity, 24=segment, 25=calc metric, 100=metadata)' },
            cellStyle: getCellStyle('eventType'),
        },
        {
            headerName: UI_CONTENT.headers.group_by,
            field: 'group_by',
            editable: params => !params.data?.is_deleted && params.data?.eventType !== '100',
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['user', 'project'] },
            width: 140,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'How the data is grouped (e.g., by user or project/activity).' },
            cellStyle: getCellStyle('group_by'),
        },
        {
            headerName: UI_CONTENT.headers.event_filters,
            field: 'event_filters',
            editable: params => !params.data?.is_deleted,
            cellRenderer: EventFilterRenderer,
            cellRendererParams: {
                clickToSelect: UI_CONTENT.click_to_select,
                eventFiltersList: eventFiltersList
            },
            cellEditor: 'EventFilterEditor',
            cellEditorParams: {
                title: UI_CONTENT.select_filters_title,
                doneText: UI_CONTENT.done_btn,
                eventFiltersList: eventFiltersList
            },
            cellEditorPopup: true,
            minWidth: 220,
            flex: 1,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'Which events to include in the calculation (e.g., create, update, patch).' },
            cellStyle: getCellStyle('event_filters'),
        },
        {
            headerName: UI_CONTENT.headers.display_mode,
            field: 'display_mode',
            editable: params => !params.data?.is_deleted,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['list', 'summary'] },
            width: 100,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'List means preview card with top most data list like top 5 most logins. Summary means we show only 1 row with total data in card preview.' },
            cellStyle: getCellStyle('display_mode'),
        },
        {
            headerName: UI_CONTENT.headers.metadata_key,
            field: 'metadata_key',
            editable: params => !params.data?.is_deleted && params.data?.eventType === '100',
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: metadataKeys },
            width: 145,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'The specific metadata field to group by (only applies when Event Type is 100).' },
            cellStyle: getCellStyle('metadata_key'),
        },
        {
            headerName: UI_CONTENT.headers.is_visible,
            field: 'is_visible',
            editable: params => !params.data?.is_deleted,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: [true, false] },
            cellRenderer: VisibleRenderer,
            cellRendererParams: {
                visibleLabel: UI_CONTENT.visible_label,
                hiddenLabel: UI_CONTENT.hidden_label
            },
            width: 105,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'Whether the card is visible on the dashboard.' },
            cellStyle: getCellStyle('is_visible'),
        },
        {
            headerName: UI_CONTENT.headers.width, field: 'width',
            editable: params => !params.data?.is_deleted,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['half', 'full'] },
            width: 100,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'The width of the card (half or full width).' },
            cellStyle: getCellStyle('width'),
        },
        {
            headerName: UI_CONTENT.headers.color || 'Color',
            field: 'color',
            editable: params => !params.data?.is_deleted,
            width: 120,
            cellRenderer: ColorCellRenderer,
            cellEditor: 'ColorCellEditor',
            cellEditorPopup: true,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'Hex accent color used for the card bullet and progress bars.' },
            cellStyle: getCellStyle('color'),
        },
        {
            headerName: UI_CONTENT.headers.count_header, field: 'count_header',
            editable: params => !params.data?.is_deleted, width: 140,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'The header text for the count column in the detail view.' },
            cellStyle: getCellStyle('count_header'),
        },
        {
            headerName: UI_CONTENT.headers.total_count_header, field: 'total_count_header',
            editable: params => !params.data?.is_deleted, width: 160,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'The header text for the total count column in the detail view.' },
            cellStyle: getCellStyle('total_count_header'),
        },
        {
            headerName: UI_CONTENT.headers.help_text, field: 'help_text',
            editable: params => !params.data?.is_deleted, flex: 1, minWidth: 200,
            headerComponent: CustomHeader,
            headerComponentParams: { tooltipText: 'The tooltip text shown on the card itself.' },
            cellStyle: getCellStyle('help_text'),
        },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 100,
            pinned: 'right',
            filter: false,
            sortable: false,
            resizable: false,
            cellRenderer: (params) => {
                if (!params.data) return null;

                if (params.data.is_deleted) {
                    return (
                        <div className="flex items-center h-full">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    params.node.data.is_deleted = false;
                                    params.api.redrawRows();
                                }}
                                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded bg-red-50 text-red-600 hover:bg-red-100 cursor-pointer border border-red-200"
                                title="Restore this card"
                            >
                                <RotateCcw className="w-3 h-3" />
                                Undo
                            </button>
                        </div>
                    );
                }

                return (
                    <div className="flex items-center gap-1 h-full">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAddRowBelow(params.node);
                            }}
                            className="p-1.5 text-gray-500 hover:text-primary hover:bg-indigo-50 rounded-md transition-colors cursor-pointer"
                            title="Add card below"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSingleRowDeleteClick(params.node);
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                            title="Delete card"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                );
            }
        }], [metadataKeys, getCellStyle, UI_CONTENT, eventFiltersList, handleSingleRowDeleteClick, handleAddRowBelow]);

    return (
        <PrimaryModal title={UI_CONTENT.title} hideModal={closeManageCardsModal} widthClass="max-w-7xl">
            {/*
              Fix: use a fixed-height flex column so the footer is always visible.
              The grid area scrolls internally; the footer stays pinned at bottom.
            */}
            <div style={{ display: 'flex', flexDirection: 'column', height: 620 }}>

                {/* ── Toolbar ── */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 24px 12px', borderBottom: '1px solid #f3f4f6', flexShrink: 0,
                }}>
                    <p style={{ fontSize: 13, color: '#6b7280' }}>
                        {loading
                            ? UI_CONTENT.loading_cards
                            : `${rowData.length} card${rowData.length !== 1 ? 's' : ''} · ${UI_CONTENT.double_click_instruction}`}
                    </p>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {selectedCount > 0 && (
                            <button
                                onClick={handleDeleteButtonClick}
                                disabled={saving}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '6px 12px', fontSize: 13, fontWeight: 500,
                                    color: '#dc2626', background: '#fef2f2',
                                    border: '1px solid #fecaca', borderRadius: 8,
                                    cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1,
                                }}
                            >
                                {`${UI_CONTENT.delete_btn} (${selectedCount})`}
                            </button>
                        )}
                        <PrimaryButton
                            handleClick={handleAddCard}
                            disable={loading || saving}
                            size="sm"
                            className="whitespace-nowrap"
                        >
                            {UI_CONTENT.add_card_btn}
                        </PrimaryButton>
                    </div>
                </div>

                {/* ── Grid area — scrollable ── */}
                <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
                            <Loader />
                            <p style={{ fontSize: 13, color: '#9ca3af' }}>{UI_CONTENT.fetching_cards}</p>
                        </div>
                    ) : rowData.length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <p style={{ fontSize: 13, color: '#9ca3af' }}>{UI_CONTENT.no_cards_msg}</p>
                        </div>
                    ) : (
                        <div style={{ width: '100%', height: '100%' }}>
                            <AgGridWrapper
                                tableId="manage-dashboard-cards"
                                rowData={rowData}
                                columnDefs={columnDefs}
                                onGridReady={params => setGridApi(params.api)}
                                onSelectionChanged={onSelectionChanged}
                                onCellValueChanged={onCellValueChanged}
                                domLayout="autoHeight"
                                pagination={false}
                                rowSelection="multiple"
                                stopEditingWhenCellsLoseFocus={false}
                                suppressRowClickSelection={true}
                                animateRows={true}
                                rowDragManaged={true}
                                onRowDragEnd={onRowDragEnd}
                                onColumnResized={params => params.api.refreshCells({ columns: ['event_filters'], force: true })}
                                components={{ EventFilterEditor, ColorCellEditor, CustomHeader }}
                            />
                        </div>
                    )}
                </div>

                {/* ── Footer — always visible ── */}
                <div style={{
                    display: 'flex', justifyContent: 'flex-end', gap: 12,
                    padding: '14px 24px', borderTop: '1px solid #f3f4f6', flexShrink: 0,
                    background: '#fff',
                }}>
                    <SecondaryButton handleClick={closeManageCardsModal} disable={saving} size="base">
                        {UI_CONTENT.cancel_btn}
                    </SecondaryButton>
                    <PrimaryButton handleClick={handleSave} disable={saving || loading} size="base">
                        {saving ? UI_CONTENT.saving_btn : UI_CONTENT.save_btn}
                    </PrimaryButton>
                </div>
            </div>

            {/* ── Delete Confirmation Modal ── */}
            {showDeleteConfirmModal && (
                <ConfirmModal
                    title="Mark Cards for Deletion"
                    description={`Are you sure you want to mark ${deleteTargetNodes.length} selected card(s) for deletion? Changes will be permanently saved when you click "Save Changes".`}
                    ConfirmText="Delete"
                    hideModal={() => { setShowDeleteConfirmModal(false); setDeleteTargetNodes([]); }}
                    handleConfirmation={handleConfirmDelete}
                />
            )}
        </PrimaryModal>
    );
};

export default ManageDashboardCardsModal;
